import { Component, OnInit } from '@angular/core';
import { IUser } from 'projects/common/src/app/interfaces';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { ReportDrillingService } from '../../services/report-drilling.service';

@Component({
  selector: 'app-report-drilling',
  templateUrl: './report-drilling.component.html',
  styleUrls: ['./report-drilling.component.scss'],
})
export class ReportDrillingComponent implements OnInit {
  public message: string;
  public tableConfig: ITableConfig;
  public tableByMachine: ITableConfig;
  public params: IOutputFilter;
  public reportData: any;
  public hideReport: boolean;
  public hasData: boolean;
  public systemError: string;
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private layoutConfigService: LayoutConfigService,
    private languageService: LanguageService,
    private cacheService: CacheService,
    private reportDrillingService: ReportDrillingService,
    private tableConfigService: TableConfigService,
    private datePipe: DatePresentationPipe,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
  ) {
    this.initDefaultFilterMachine();
    this.hideReport = true;
    this.hasData = false;
    this.disabledExcelBtn = false;
  }

  /**
   * init default Filter machine
   */
  public initDefaultFilterMachine(): void {
    const allMachines = this.cacheService.session('machineCollection') as IMachineContext[];
    const selected = allMachines.find((v) => v.code === 'JMB');

    this.filterConfig = {
      dates: { required: true },
      shift: { required: true },
      machineType: { required: true, uniqueSelection: selected },
      machineList: { required: true, multiselect: true },
      cleanButton: true,
    };
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'report-drilling',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
  }

  /**
   * get report drilling
   */
  public getReportFiltered($event: IOutputFilter): void {
    const user = this.cacheService.local('session').user as IUser;
    this.filterConfig.isLoading = true;

    this.params = {
      startDate: $event.startDate,
      endDate: $event.endDate,
      shiftId: $event.shiftId,
      machineTypeId: $event.machineTypeId,
      machineId: $event.machineId,
      companyId: user.company.id,
    };

    this.reportDrillingService.GetDrillingReportData(this.params).then(
      (response) => {
        this.reportData = response;

        if (
          this.reportData &&
          this.reportData.totalDepth.length &&
          this.reportData.totalByMachine.length &&
          this.reportData.drillGrouped.length
        ) {
          this.message = '';
          this.setTableConfig(this.reportData.drillGrouped);
          this.setTableByMachine(this.reportData.totalByMachine);
          this.hideReport = false;
          this.hasData = true;
        } else {
          this.message = this.languageService.message('m034');
          this.hideReport = true;
          this.hasData = false;
        }
        this.filterConfig.isLoading = false;

        this.systemError = '';
        this.setTableConfig(this.reportData.drillGrouped);
        this.setTableByMachine(this.reportData.totalByMachine);
      },
      ({ name, message }) => {
        console.error(message);
        this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
        this.hideReport = true;
        this.hasData = false;
        this.filterConfig.isLoading = false;
      },
    );
    const filters = {
      reports: 'report-drilling',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to set a table by config
   */
  private setTableConfig(tableData: any[]): void {
    const excludeColumns = ['machineId', 'shiftId', 'operatorName', 'ZoneName'];

    // Create config of headers
    const headerParams: ITableHeaderParams = {
      data: tableData,
      excludeColumns,
      replaceColumnName: [
        { prevName: 'countDisplacementNumber', newName: 'drillings' },
        { prevName: 'depth', newName: 'mts-drill' },
        { prevName: 'displacementNumber', newName: 'stick' },
        { prevName: 'operatorName', newName: 'operator' },
      ],
      sortables: true,
    };

    // Create config of dataset
    const datasetParams: ITableDataParams = {
      tableData,
      excludeColumns,
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableConfig = null;
    setTimeout(() => (this.tableConfig = { header, dataset }), 0);
  }

  /**
   * Function to set a table by config
   */
  private setTableByMachine(tableData: any[]): void {
    const extraParams: ITableExtraParams = {
      minimalRows: 6,
      disableFooter: true,
    };

    const headerParams: ITableHeaderParams = {
      data: tableData,
      replaceColumnName: [
        { prevName: 'totalCountDisplacementNumber', newName: 'drillings' },
        { prevName: 'totalDepth', newName: 'mts-drill' },
      ],
    };

    // Create config of dataset
    const datasetParams: ITableDataParams = {
      tableData,
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableByMachine = null;
    setTimeout(() => (this.tableByMachine = { header, dataset, extraParams }), 0);
  }

  /**
   * Download excel file for report fronts
   */
  public downloadExcel(): void {
    const params = {
      startDate: this.params.startDate,
      endDate: this.params.endDate,
      shiftId: this.params.shiftId,
      machineType: this.params.machineTypeId,
      machineList: this.params.machineId,
    };
    const selectRangeDate = {
      startDate: this.datePipe.transform(this.params.startDate),
      endDate: this.datePipe.transform(this.params.endDate),
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.reportDrillingService.getDrillingReportExcel(params).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'drilling-report', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
